/**
 * Route : profil de routing nommé et réutilisable (projection UI de
 * routeprofile.Profile backend). Capture une table de routing complète
 * (cascade par tier + poids + stratégie de load balancing) sous un nom ; une
 * APIKey la référence via route_id (sa route EFFECTIVE au runtime). Réutilise
 * les value objects routing du domaine api-keys.
 */
import type { RoutingMap, RoutingStrategyMap } from '@/domain/api-keys'

export interface Route {
    readonly id: string
    readonly tenantId: string
    readonly name: string
    readonly description: string
    readonly routing: RoutingMap
    readonly routingStrategies?: RoutingStrategyMap
    readonly createdAt: string
}

export interface CreateRouteInput {
    readonly name: string
    readonly description?: string
    readonly tenantId?: string
    readonly routing: RoutingMap
    readonly routingStrategies?: RoutingStrategyMap
}

export interface UpdateRouteInput {
    readonly name: string
    readonly description: string
    readonly routing: RoutingMap
    readonly routingStrategies?: RoutingStrategyMap
}

/** Valeurs émises par le formulaire de route (create + edit partagent la vue). */
export interface RouteFormValues {
    readonly name: string
    readonly description: string
    readonly routing: RoutingMap
    readonly routingStrategies: RoutingStrategyMap
}

export type RouteErrorCode =
    | 'invalid_name'
    | 'route_exists'
    | 'route_not_found'
    | 'empty_routing'
    | 'invalid_routing'
    | 'tenant_not_found'
    | 'tenant_scope'
    | 'csrf_required'
    | 'csrf_invalid'
    | 'unauthorized'
    | 'forbidden'
    | 'invalid_body'
    | 'internal_error'
    | 'network_error'
    | 'unknown'

export class RouteError extends Error {
    readonly code: RouteErrorCode
    readonly httpStatus: number | null
    constructor(code: RouteErrorCode, message: string, httpStatus: number | null = null) {
        super(message)
        this.code = code
        this.httpStatus = httpStatus
        this.name = 'RouteError'
    }
}

export function isRouteError(err: unknown): err is RouteError {
    return err instanceof RouteError
}

export const KNOWN_ROUTE_CODES: readonly RouteErrorCode[] = [
    'invalid_name',
    'route_exists',
    'route_not_found',
    'empty_routing',
    'invalid_routing',
    'tenant_not_found',
    'tenant_scope',
    'csrf_required',
    'csrf_invalid',
    'unauthorized',
    'forbidden',
    'invalid_body',
    'internal_error',
    'network_error',
    'unknown',
] as const
